import type { Language } from "./enums";

export const CHASSIS_CODE_LIST_IDS = ["bmw", "bmw_m", "mini", "motorrad"] as const;
export type ChassisCodeListId = (typeof CHASSIS_CODE_LIST_IDS)[number];

export interface ChassisCodeEntry {
  /**
   * The manufacturer's development code, e.g. "E90", or a body sub-code such as "E46/2". A
   * variant whose parts don't interchange with the base model carries its model in brackets:
   * after BMW's own code where one is known ("E36/2S (M3)"), else after the shared code
   * ("E92 (M3)", "G26 (i4)", "K50 (R1250GS)").
   */
  code: string;
  model: Record<Language, string>;
  /** Approximate production years; `to` is null while the model is still built. */
  from: number;
  to: number | null;
  /** A body sub-code's generation: parts marked with it fit this body too ("E46" for "E46/2"). */
  parent?: string;
}

function entry(
  code: string,
  en: string,
  de: string,
  from: number,
  to: number | null,
  parent?: string,
): ChassisCodeEntry {
  return parent
    ? { code, model: { en, de }, from, to, parent }
    : { code, model: { en, de }, from, to };
}

/** Motorcycle names are the same in every language. */
function bike(code: string, name: string, from: number, to: number | null): ChassisCodeEntry {
  return entry(code, name, name, from, to);
}

/**
 * Built-in chassis codes the user can pre-fill from. Compiled in October 2026 from Wikipedia's
 * BMW and BMW M model lists and generation pages (body sub-codes), MotoringFile's MINI code table
 * and the RealOEM/bimmerarchiv parts catalogue data for BMW Motorrad; codes no source confirmed
 * are left out (e.g. F81 was never built). Each code means one model: M cars, electric twins and
 * motorcycles that share a code with another model carry their model in brackets, so no code
 * appears in two lists.
 */
export const CHASSIS_CODE_LISTS: Record<ChassisCodeListId, readonly ChassisCodeEntry[]> = {
  bmw: [
    entry("E3", "New Six saloon (2500–3.3)", "Große Limousine (2500–3.3)", 1968, 1977),
    entry("E9", "New Six coupé (2800 CS–3.0 CSL)", "Großes Coupé (2800 CS–3.0 CSL)", 1968, 1975),
    entry("E10", "02 Series (1602–2002)", "02er (1602–2002)", 1966, 1977),
    entry("E12", "5 Series", "5er", 1972, 1981),
    entry("E21", "3 Series", "3er", 1975, 1983),
    entry("E23", "7 Series", "7er", 1977, 1987),
    entry("E24", "6 Series", "6er", 1976, 1989),
    entry("E28", "5 Series", "5er", 1981, 1988),
    entry("E30", "3 Series (all bodies)", "3er (alle Karosserien)", 1982, 1994),
    entry("E30/2", "3 Series 2-door Saloon", "3er Zweitürer", 1982, 1991, "E30"),
    entry("E30/2C", "3 Series Convertible", "3er Cabrio", 1985, 1993, "E30"),
    entry("E30/4", "3 Series 4-door Saloon", "3er Viertürer", 1983, 1992, "E30"),
    entry("E30/5", "3 Series Touring", "3er Touring", 1987, 1994, "E30"),
    entry("E30/16", "325iX (all-wheel drive)", "325iX (Allrad)", 1985, 1991, "E30"),
    entry("E31", "8 Series", "8er", 1989, 1999),
    entry("E32", "7 Series", "7er", 1986, 1994),
    entry("E34", "5 Series", "5er", 1987, 1996),
    entry("E36", "3 Series (all bodies)", "3er (alle Karosserien)", 1990, 2000),
    entry("E36/2", "3 Series Coupé", "3er Coupé", 1992, 1999, "E36"),
    entry("E36/2C", "3 Series Convertible", "3er Cabrio", 1993, 1999, "E36"),
    entry("E36/3", "3 Series Touring", "3er Touring", 1995, 1999, "E36"),
    entry("E36/4", "3 Series Saloon", "3er Limousine", 1990, 1998, "E36"),
    entry("E36/5", "3 Series Compact", "3er Compact", 1994, 2000),
    entry("E36/7", "Z3 Roadster", "Z3 Roadster", 1995, 2002),
    entry("E36/8", "Z3 Coupé", "Z3 Coupé", 1998, 2002),
    entry("E38", "7 Series", "7er", 1994, 2001),
    entry("E39", "5 Series", "5er", 1995, 2004),
    entry("E46", "3 Series (all bodies)", "3er (alle Karosserien)", 1998, 2006),
    entry("E46/2", "3 Series Coupé", "3er Coupé", 1999, 2006, "E46"),
    entry("E46/2C", "3 Series Convertible", "3er Cabrio", 2000, 2006, "E46"),
    entry("E46/3", "3 Series Touring", "3er Touring", 1999, 2005, "E46"),
    entry("E46/4", "3 Series Saloon", "3er Limousine", 1998, 2005, "E46"),
    entry("E46/5", "3 Series Compact", "3er Compact", 2001, 2004),
    entry("E52", "Z8", "Z8", 2000, 2003),
    entry("E53", "X5", "X5", 1999, 2006),
    entry("E60", "5 Series Saloon", "5er Limousine", 2003, 2010),
    entry("E61", "5 Series Touring", "5er Touring", 2004, 2010),
    entry("E63", "6 Series Coupé", "6er Coupé", 2003, 2010),
    entry("E64", "6 Series Convertible", "6er Cabrio", 2004, 2010),
    entry("E65", "7 Series", "7er", 2001, 2008),
    entry("E66", "7 Series long wheelbase", "7er Langversion", 2001, 2008),
    entry("E70", "X5", "X5", 2006, 2013),
    entry("E71", "X6", "X6", 2008, 2014),
    entry("E81", "1 Series 3-door", "1er Dreitürer", 2007, 2011),
    entry("E82", "1 Series Coupé", "1er Coupé", 2007, 2013),
    entry("E83", "X3", "X3", 2003, 2010),
    entry("E84", "X1", "X1", 2009, 2015),
    entry("E85", "Z4 Roadster", "Z4 Roadster", 2002, 2008),
    entry("E86", "Z4 Coupé", "Z4 Coupé", 2006, 2008),
    entry("E87", "1 Series 5-door", "1er Fünftürer", 2004, 2011),
    entry("E88", "1 Series Convertible", "1er Cabrio", 2008, 2013),
    entry("E89", "Z4 Roadster", "Z4 Roadster", 2009, 2016),
    entry("E90", "3 Series Saloon", "3er Limousine", 2005, 2012),
    entry("E91", "3 Series Touring", "3er Touring", 2005, 2012),
    entry("E92", "3 Series Coupé", "3er Coupé", 2006, 2013),
    entry("E93", "3 Series Convertible", "3er Cabrio", 2007, 2013),
    entry("F01", "7 Series", "7er", 2008, 2015),
    entry("F02", "7 Series long wheelbase", "7er Langversion", 2008, 2015),
    entry("F06", "6 Series Gran Coupé", "6er Gran Coupé", 2012, 2018),
    entry("F07", "5 Series Gran Turismo", "5er Gran Turismo", 2009, 2017),
    entry("F10", "5 Series Saloon", "5er Limousine", 2010, 2017),
    entry("F11", "5 Series Touring", "5er Touring", 2010, 2017),
    entry("F12", "6 Series Convertible", "6er Cabrio", 2011, 2018),
    entry("F13", "6 Series Coupé", "6er Coupé", 2011, 2017),
    entry("F15", "X5", "X5", 2013, 2018),
    entry("F16", "X6", "X6", 2014, 2019),
    entry("F20", "1 Series 5-door", "1er Fünftürer", 2011, 2019),
    entry("F21", "1 Series 3-door", "1er Dreitürer", 2012, 2019),
    entry("F22", "2 Series Coupé", "2er Coupé", 2014, 2021),
    entry("F23", "2 Series Convertible", "2er Cabrio", 2015, 2021),
    entry("F25", "X3", "X3", 2010, 2017),
    entry("F26", "X4", "X4", 2014, 2018),
    entry("F30", "3 Series Saloon", "3er Limousine", 2012, 2019),
    entry("F31", "3 Series Touring", "3er Touring", 2012, 2019),
    entry("F32", "4 Series Coupé", "4er Coupé", 2013, 2020),
    entry("F33", "4 Series Convertible", "4er Cabrio", 2014, 2020),
    entry("F34", "3 Series Gran Turismo", "3er Gran Turismo", 2013, 2020),
    entry("F36", "4 Series Gran Coupé", "4er Gran Coupé", 2014, 2020),
    entry("F39", "X2", "X2", 2018, 2023),
    entry("F40", "1 Series", "1er", 2019, 2024),
    entry("F44", "2 Series Gran Coupé", "2er Gran Coupé", 2020, 2024),
    entry("F45", "2 Series Active Tourer", "2er Active Tourer", 2014, 2021),
    entry("F46", "2 Series Gran Tourer", "2er Gran Tourer", 2015, 2022),
    entry("F48", "X1", "X1", 2015, 2022),
    entry("F70", "1 Series", "1er", 2024, null),
    entry("F74", "2 Series Gran Coupé", "2er Gran Coupé", 2024, null),
    entry("G01", "X3", "X3", 2017, 2024),
    entry("G02", "X4", "X4", 2018, 2025),
    entry("G05", "X5", "X5", 2018, 2026),
    entry("G06", "X6", "X6", 2019, null),
    entry("G07", "X7", "X7", 2018, null),
    entry("G08", "iX3", "iX3", 2020, 2025),
    entry("G11", "7 Series", "7er", 2015, 2022),
    entry("G12", "7 Series long wheelbase", "7er Langversion", 2015, 2022),
    entry("G14", "8 Series Convertible", "8er Cabrio", 2018, 2026),
    entry("G15", "8 Series Coupé", "8er Coupé", 2018, 2026),
    entry("G16", "8 Series Gran Coupé", "8er Gran Coupé", 2019, 2026),
    entry("G20", "3 Series Saloon", "3er Limousine", 2019, 2026),
    entry("G21", "3 Series Touring", "3er Touring", 2019, null),
    entry("G22", "4 Series Coupé", "4er Coupé", 2020, null),
    entry("G23", "4 Series Convertible", "4er Cabrio", 2021, null),
    entry("G26", "4 Series Gran Coupé", "4er Gran Coupé", 2021, null),
    entry("G26 (i4)", "i4", "i4", 2021, null),
    entry("G29", "Z4 Roadster", "Z4 Roadster", 2018, 2026),
    entry("G30", "5 Series Saloon", "5er Limousine", 2017, 2023),
    entry("G31", "5 Series Touring", "5er Touring", 2017, 2024),
    entry("G32", "6 Series Gran Turismo", "6er Gran Turismo", 2017, 2023),
    entry("G42", "2 Series Coupé", "2er Coupé", 2021, null),
    entry("G45", "X3", "X3", 2024, null),
    entry("G50", "3 Series Saloon", "3er Limousine", 2026, null),
    entry("G60", "5 Series Saloon", "5er Limousine", 2023, null),
    entry("G60 (i5)", "i5 Saloon", "i5 Limousine", 2023, null),
    entry("G61", "5 Series Touring", "5er Touring", 2024, null),
    entry("G61 (i5)", "i5 Touring", "i5 Touring", 2024, null),
    entry("G65", "X5", "X5", 2026, null),
    entry("G70", "7 Series", "7er", 2022, null),
    entry("G70 (i7)", "i7", "i7", 2022, null),
    entry("I01", "i3", "i3", 2013, 2022),
    entry("I12", "i8 Coupé", "i8 Coupé", 2014, 2020),
    entry("I15", "i8 Roadster", "i8 Roadster", 2018, 2020),
    entry("I20", "iX", "iX", 2021, null),
    entry("NA0", "i3 (Neue Klasse)", "i3 (Neue Klasse)", 2026, null),
    entry("NA5", "iX3 (Neue Klasse)", "iX3 (Neue Klasse)", 2025, null),
    entry("U06", "2 Series Active Tourer", "2er Active Tourer", 2021, null),
    entry("U10", "X2", "X2", 2023, null),
    entry("U10 (iX2)", "iX2", "iX2", 2023, null),
    entry("U11", "X1", "X1", 2022, null),
    entry("U11 (iX1)", "iX1", "iX1", 2022, null),
  ],
  bmw_m: [
    entry("E26", "M1", "M1", 1978, 1981),
    entry("E24 (M6)", "M635 CSi, M6", "M635 CSi, M6", 1984, 1989),
    entry("E28S (M5)", "M5", "M5", 1985, 1988),
    entry("E30 (M3)", "M3 (all bodies)", "M3 (alle Karosserien)", 1986, 1991),
    entry("E30/2S (M3)", "M3", "M3", 1986, 1990, "E30 (M3)"),
    entry("E30/2SC (M3)", "M3 Convertible", "M3 Cabrio", 1988, 1991, "E30 (M3)"),
    entry("E34 (M5)", "M5", "M5", 1988, 1995),
    entry("E36 (M3)", "M3 (all bodies)", "M3 (alle Karosserien)", 1992, 1999),
    entry("E36/2S (M3)", "M3 Coupé", "M3 Coupé", 1992, 1999, "E36 (M3)"),
    entry("E36/2CS (M3)", "M3 Convertible", "M3 Cabrio", 1994, 1999, "E36 (M3)"),
    entry("E36/4S (M3)", "M3 Saloon", "M3 Limousine", 1994, 1998, "E36 (M3)"),
    entry("E36/7 (Z3 M)", "Z3 M Roadster", "Z3 M Roadster", 1996, 2002),
    entry("E36/8 (Z3 M)", "Z3 M Coupé", "Z3 M Coupé", 1998, 2002),
    entry("E39S (M5)", "M5", "M5", 1998, 2003),
    entry("E46 (M3)", "M3", "M3", 2000, 2006),
    entry("E60 (M5)", "M5 Saloon", "M5 Limousine", 2005, 2010),
    entry("E61 (M5)", "M5 Touring", "M5 Touring", 2007, 2010),
    entry("E63 (M6)", "M6 Coupé", "M6 Coupé", 2005, 2010),
    entry("E64 (M6)", "M6 Convertible", "M6 Cabrio", 2006, 2010),
    entry("E70 (X5 M)", "X5 M", "X5 M", 2009, 2013),
    entry("E71 (X6 M)", "X6 M", "X6 M", 2009, 2014),
    entry("E82 (1M)", "1 Series M Coupé", "1er M Coupé", 2011, 2012),
    entry("E85 (Z4 M)", "Z4 M Roadster", "Z4 M Roadster", 2006, 2008),
    entry("E86 (Z4 M)", "Z4 M Coupé", "Z4 M Coupé", 2006, 2008),
    entry("E90 (M3)", "M3 Saloon", "M3 Limousine", 2007, 2011),
    entry("E92 (M3)", "M3 Coupé", "M3 Coupé", 2007, 2013),
    entry("E93 (M3)", "M3 Convertible", "M3 Cabrio", 2008, 2013),
    entry("F06 (M6)", "M6 Gran Coupé", "M6 Gran Coupé", 2013, 2018),
    entry("F10 (M5)", "M5", "M5", 2011, 2016),
    entry("F12 (M6)", "M6 Convertible", "M6 Cabrio", 2012, 2018),
    entry("F13 (M6)", "M6 Coupé", "M6 Coupé", 2012, 2017),
    entry("F80", "M3", "M3", 2014, 2018),
    entry("F82", "M4 Coupé", "M4 Coupé", 2014, 2020),
    entry("F83", "M4 Convertible", "M4 Cabrio", 2014, 2020),
    entry("F85", "X5 M", "X5 M", 2014, 2018),
    entry("F86", "X6 M", "X6 M", 2014, 2019),
    entry("F87", "M2", "M2", 2015, 2021),
    entry("F90", "M5", "M5", 2017, 2023),
    entry("F91", "M8 Convertible", "M8 Cabrio", 2019, 2025),
    entry("F92", "M8 Coupé", "M8 Coupé", 2019, 2025),
    entry("F93", "M8 Gran Coupé", "M8 Gran Coupé", 2019, 2025),
    entry("F95", "X5 M", "X5 M", 2020, null),
    entry("F96", "X6 M", "X6 M", 2020, null),
    entry("F97", "X3 M", "X3 M", 2019, 2024),
    entry("F98", "X4 M", "X4 M", 2019, 2025),
    entry("G09", "XM", "XM", 2022, null),
    entry("G80", "M3 Saloon", "M3 Limousine", 2021, null),
    entry("G81", "M3 Touring", "M3 Touring", 2022, null),
    entry("G82", "M4 Coupé", "M4 Coupé", 2021, null),
    entry("G83", "M4 Convertible", "M4 Cabrio", 2021, null),
    entry("G87", "M2", "M2", 2023, null),
    entry("G90", "M5 Saloon", "M5 Limousine", 2024, null),
    entry("G99", "M5 Touring", "M5 Touring", 2024, null),
  ],
  mini: [
    entry("R50", "One, Cooper (3-door)", "One, Cooper (3-Türer)", 2001, 2006),
    entry("R52", "Convertible", "Cabrio", 2004, 2008),
    entry("R53", "Cooper S (3-door)", "Cooper S (3-Türer)", 2001, 2006),
    entry("R55", "Clubman", "Clubman", 2007, 2014),
    entry("R56", "3-door", "3-Türer", 2006, 2014),
    entry("R57", "Convertible", "Cabrio", 2008, 2015),
    entry("R58", "Coupé", "Coupé", 2011, 2015),
    entry("R59", "Roadster", "Roadster", 2011, 2015),
    entry("R60", "Countryman", "Countryman", 2010, 2016),
    entry("R61", "Paceman", "Paceman", 2012, 2016),
    entry("F54", "Clubman", "Clubman", 2015, 2024),
    entry("F55", "5-door", "5-Türer", 2014, 2024),
    entry("F56", "3-door", "3-Türer", 2014, 2024),
    entry("F57", "Convertible", "Cabrio", 2016, 2024),
    entry("F60", "Countryman", "Countryman", 2016, 2024),
    entry("F65", "Cooper 5-door", "Cooper 5-Türer", 2024, null),
    entry("F66", "Cooper 3-door", "Cooper 3-Türer", 2024, null),
    entry("F67", "Cooper Convertible", "Cooper Cabrio", 2024, null),
    entry("J01", "Cooper Electric (3-door)", "Cooper Electric (3-Türer)", 2024, null),
    entry("J05", "Aceman", "Aceman", 2024, null),
    entry("U25", "Countryman", "Countryman", 2024, null),
  ],
  motorrad: [
    entry("R13 (F650GS)", "F 650 GS (single)", "F 650 GS (Einzylinder)", 2000, 2007),
    bike("R13 (G650GS)", "G 650 GS", 2009, 2015),
    bike("R21 (R1150GS)", "R 1150 GS", 1999, 2004),
    bike("R21 (R1150GSA)", "R 1150 GS Adventure", 2001, 2005),
    bike("K02", "G 310 GS", 2017, null),
    bike("K03", "G 310 R", 2016, null),
    bike("K08", "C 400 GT", 2019, null),
    bike("K09", "C 400 X", 2018, null),
    bike("K17", "C evolution", 2014, 2021),
    bike("K18 (C600Sport)", "C 600 Sport", 2012, 2015),
    bike("K18 (C650Sport)", "C 650 Sport", 2015, 2020),
    bike("K19", "C 650 GT", 2012, 2020),
    bike("K21", "R nineT", 2014, 2024),
    bike("K22", "R nineT Pure", 2017, 2024),
    bike("K23", "R nineT Scrambler", 2016, 2024),
    bike("K25 (R1200GS)", "R 1200 GS", 2004, 2012),
    bike("K25 (HP2Enduro)", "HP2 Enduro", 2005, 2008),
    bike("K25 (HP2Megamoto)", "HP2 Megamoto", 2007, 2010),
    bike("K255", "R 1200 GS Adventure", 2005, 2013),
    bike("K26 (R1200RT)", "R 1200 RT", 2005, 2013),
    bike("K26 (R900RT)", "R 900 RT", 2005, 2013),
    bike("K27", "R 1200 R", 2006, 2014),
    bike("K28", "R 1200 ST", 2005, 2007),
    bike("K29 (R1200S)", "R 1200 S", 2006, 2008),
    bike("K29 (HP2Sport)", "HP2 Sport", 2008, 2010),
    bike("K32", "R nineT Racer", 2017, 2019),
    bike("K33", "R nineT Urban G/S", 2017, 2023),
    bike("K34", "R 18", 2020, null),
    bike("K40 (K1200S)", "K 1200 S", 2004, 2008),
    bike("K40 (K1300S)", "K 1300 S", 2009, 2015),
    bike("K41 (K1200RS)", "K 1200 RS", 1996, 2005),
    bike("K41 (K1200GT)", "K 1200 GT", 2002, 2005),
    bike("K43 (K1200R)", "K 1200 R", 2005, 2008),
    bike("K43 (K1300R)", "K 1300 R", 2009, 2015),
    bike("K44 (K1200GT)", "K 1200 GT", 2006, 2008),
    bike("K44 (K1300GT)", "K 1300 GT", 2009, 2011),
    bike("K46 (S1000RR)", "S 1000 RR", 2009, 2018),
    bike("K46 (HP4)", "HP4", 2012, 2014),
    bike("K47", "S 1000 R", 2014, 2020),
    bike("K48 (K1600GT)", "K 1600 GT", 2011, null),
    bike("K48 (K1600GTL)", "K 1600 GTL", 2011, null),
    bike("K49", "S 1000 XR", 2015, 2019),
    bike("K50 (R1200GS)", "R 1200 GS", 2013, 2018),
    bike("K50 (R1250GS)", "R 1250 GS", 2019, 2023),
    bike("K51 (R1200GSA)", "R 1200 GS Adventure", 2014, 2018),
    bike("K51 (R1250GSA)", "R 1250 GS Adventure", 2019, 2023),
    bike("K52 (R1200RT)", "R 1200 RT", 2014, 2018),
    bike("K52 (R1250RT)", "R 1250 RT", 2019, 2024),
    bike("K53 (R1200R)", "R 1200 R", 2015, 2018),
    bike("K53 (R1250R)", "R 1250 R", 2019, 2023),
    bike("K54 (R1200RS)", "R 1200 RS", 2015, 2018),
    bike("K54 (R1250RS)", "R 1250 RS", 2019, 2023),
    bike("K60", "HP4 Race", 2017, 2017),
    bike("K63", "S 1000 R", 2021, null),
    bike("K67", "S 1000 RR", 2019, null),
    bike("K69", "S 1000 XR", 2020, null),
    bike("K70", "F 700 GS", 2012, 2018),
    bike("K71 (F800S)", "F 800 S", 2006, 2010),
    bike("K71 (F800ST)", "F 800 ST", 2006, 2012),
    bike("K71 (F800GT)", "F 800 GT", 2013, 2019),
    entry("K72 (F650GS)", "F 650 GS (twin)", "F 650 GS (Zweizylinder)", 2008, 2012),
    bike("K72 (F800GS)", "F 800 GS", 2008, 2018),
    bike("K73", "F 800 R", 2009, 2019),
    bike("K80 (F750GS)", "F 750 GS", 2018, 2023),
    bike("K80 (F800GS)", "F 800 GS", 2024, null),
    bike("K81", "F 850 GS", 2018, 2023),
    bike("K82 (F850GSA)", "F 850 GS Adventure", 2019, 2023),
    bike("K82 (F900GSA)", "F 900 GS Adventure", 2024, null),
    bike("KA1", "R 1300 GS", 2023, null),
  ],
};

function squash(code: string): string {
  return code.replace(/[\s()-]+/g, "").toUpperCase();
}

/**
 * BMW's own variant code without the model in brackets ("E36/2S" for "E36/2S (M3)"), where that
 * bare code isn't a list code of its own (unlike "E92" next to "E92 (M3)").
 */
const ALIASES = (() => {
  const entries = CHASSIS_CODE_LIST_IDS.flatMap((list) => CHASSIS_CODE_LISTS[list]);
  const codes = new Set(entries.map((e) => squash(e.code)));
  return new Map(
    entries.flatMap((e) => {
      const bare = squash(e.code.replace(/\s*\(.*\)$/, ""));
      return bare !== squash(e.code) && !codes.has(bare) ? [[bare, squash(e.code)] as const] : [];
    }),
  );
})();

/**
 * The form two spellings of one chassis code share: case, spaces, hyphens and brackets don't
 * count ("e 90" is E90; "E92-M3" and "E92 M3" are "E92 (M3)"), and BMW's bare variant code is its
 * bracketed entry ("E36/2S" is "E36/2S (M3)").
 */
export function normalizeChassisCode(code: string): string {
  const squashed = squash(code);
  return ALIASES.get(squashed) ?? squashed;
}

/** Natural order, so E9 comes before E10 and E36 before E36/7. */
export function compareChassisCodes(a: string, b: string): number {
  return a.localeCompare(b, "en", { numeric: true, sensitivity: "base" });
}

/** Model names of a code across the built-in lists ("3 Series", "M3"), empty for own codes. */
export function chassisCodeModels(code: string, language: Language): string[] {
  const key = normalizeChassisCode(code);
  const models = CHASSIS_CODE_LIST_IDS.flatMap((list) =>
    CHASSIS_CODE_LISTS[list]
      .filter((e) => normalizeChassisCode(e.code) === key)
      .map((e) => e.model[language]),
  );
  return [...new Set(models)];
}

/** Picked codes the workspace doesn't have yet, each once, compared normalized. */
export function chassisCodesToAdd(
  existingNames: readonly string[],
  picked: readonly string[],
): string[] {
  const seen = new Set(existingNames.map(normalizeChassisCode));
  const added: string[] = [];
  for (const code of picked) {
    const key = normalizeChassisCode(code);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    added.push(code.trim());
  }
  return added;
}

/** Body sub-code → its generation, from the built-in lists ("E46/2" → "E46"), normalized. */
const PARENTS = new Map(
  CHASSIS_CODE_LIST_IDS.flatMap((list) =>
    CHASSIS_CODE_LISTS[list].flatMap((e) =>
      e.parent ? [[normalizeChassisCode(e.code), normalizeChassisCode(e.parent)] as const] : [],
    ),
  ),
);

/**
 * A part or template fits a vehicle when one of its chassis codes is the vehicle's chassis code
 * or, for a body sub-code, its generation: an "E46" part fits an "E46/2" coupé, but an "E46/2"
 * part does not fit every "E46". Variants never inherit from the base model: an "E92" part does
 * not fit an "E92 (M3)", and an "E36" part does not fit an "E36/7" Z3. Codes are compared by
 * their fixed `key`, so renaming a chassis code's label never changes what fits. Returns the
 * matching code of the part or template, or null.
 */
export function fittingChassisCode<T extends { key: string }>(
  codes: readonly T[],
  vehicleKey: string | null | undefined,
): T | null {
  for (const target of fittingChassisKeys(vehicleKey)) {
    const exact = codes.find((code) => normalizeChassisCode(code.key) === target);
    if (exact) return exact;
  }
  return null;
}

/**
 * The normalized keys a part or template may carry to fit a vehicle, most specific first: the
 * vehicle's own code, then its generation ("E46/2" → ["E46/2", "E46"]).
 */
export function fittingChassisKeys(vehicleKey: string | null | undefined): string[] {
  const keys: string[] = [];
  let target = vehicleKey ? normalizeChassisCode(vehicleKey) : "";
  while (target && !keys.includes(target)) {
    keys.push(target);
    target = PARENTS.get(target) ?? "";
  }
  return keys;
}

/** The colours a chassis code can have; each is a Tailwind hue (the web app maps them). */
export const CHASSIS_CODE_COLORS = [
  "sky",
  "blue",
  "indigo",
  "cyan",
  "teal",
  "emerald",
  "green",
  "lime",
  "violet",
  "purple",
  "amber",
  "yellow",
  "orange",
  "red",
  "rose",
  "pink",
  "fuchsia",
  "slate",
  "stone",
] as const;

export type ChassisCodeColor = (typeof CHASSIS_CODE_COLORS)[number];

/**
 * Built-in codes are coloured by model series, each series a family of similar hues that its
 * generations take turns with in production order, so neighbouring generations differ. Body
 * sub-codes take their generation's colour.
 */
const SERIES_HUES = {
  compact: ["cyan", "teal"],
  three: ["sky", "blue", "indigo"],
  five: ["emerald", "green"],
  seven: ["violet", "purple"],
  x: ["amber", "yellow"],
  m: ["red", "rose"],
  mini: ["pink", "fuchsia"],
  motorrad: ["orange"],
  other: ["slate", "stone"],
} as const satisfies Record<string, readonly ChassisCodeColor[]>;

type Series = keyof typeof SERIES_HUES;

function seriesOf(list: ChassisCodeListId, entry: ChassisCodeEntry): Series {
  if (list === "bmw_m") return "m";
  if (list === "mini") return "mini";
  if (list === "motorrad") return "motorrad";
  const model = entry.model.en;
  if (/^[12] Series/.test(model)) return "compact";
  if (/^[34] Series|^325iX/.test(model)) return "three";
  if (/^[56] Series/.test(model)) return "five";
  if (/^[78] Series/.test(model)) return "seven";
  if (/^X\d/.test(model)) return "x";
  return "other";
}

const BUILT_IN_COLORS: ReadonlyMap<string, ChassisCodeColor> = (() => {
  const colors = new Map<string, ChassisCodeColor>();
  const bodies: [string, string][] = [];
  for (const list of CHASSIS_CODE_LIST_IDS) {
    const turns = new Map<Series, number>();
    for (const entry of CHASSIS_CODE_LISTS[list]) {
      const key = normalizeChassisCode(entry.code);
      if (entry.parent) {
        bodies.push([key, normalizeChassisCode(entry.parent)]);
        continue;
      }
      const series = seriesOf(list, entry);
      const hues: readonly ChassisCodeColor[] = SERIES_HUES[series];
      const turn = turns.get(series) ?? 0;
      turns.set(series, turn + 1);
      colors.set(key, hues[turn % hues.length] ?? "slate");
    }
  }
  for (const [key, parent] of bodies) colors.set(key, colors.get(parent) ?? "slate");
  return colors;
})();

/** Colours for codes not in the built-in lists: never grey, so every code stands out. */
const OWN_CODE_COLORS = CHASSIS_CODE_COLORS.filter((c) => c !== "slate" && c !== "stone");

/**
 * The colour a chassis code has unless the user picked another: from the built-in lists, or for
 * the user's own codes one derived from the code itself, so it is the same everywhere.
 */
export function defaultChassisCodeColor(key: string): ChassisCodeColor {
  const normalized = normalizeChassisCode(key);
  const builtIn = BUILT_IN_COLORS.get(normalized);
  if (builtIn) return builtIn;
  let hash = 0;
  for (const char of normalized) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  return OWN_CODE_COLORS[hash % OWN_CODE_COLORS.length] ?? "blue";
}
